import React, { useState, useMemo } from 'react';
import { DeveloperAwardType, IDeveloperAward } from 'utils/entities';
import { SmallText, Text } from 'ds/components/typography';
import Marquee from 'components/marquee';
import { SectionTitle, SubsectionWrapper, SectionWrapper } from 'screens/DeveloperPage/styled';
import { useLocale } from 'locale';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import AwardIcon from 'assets/svg/developer/award.svg';
import * as Styled from './styled';
import { useScreenBreakpoint } from 'consts/breakpoints';
import ChevronDown from 'assets/svg/chevron-down.svg';
import AnchorMiddleware from 'components/anchor';

const FOLDER = 'developer-page/';

const NON_ANIMATED_COUNT = 3;
const NON_ANIMATED_COUNT_TAB = 2;

const IMAGE_SIZE = 123;

const awardTypeToImagePathMap: Partial<Record<DeveloperAwardType, string>> = {
  [DeveloperAwardType.Madad2018]: `${FOLDER}madad-2018-award.png`,
  [DeveloperAwardType.Madad2019]: `${FOLDER}madad-2019-award.png`,
  [DeveloperAwardType.Madad2020]: `${FOLDER}madad-2020-award.png`,
  [DeveloperAwardType.Madad2021]: `${FOLDER}madad-2021-award.png`,
  [DeveloperAwardType.Madad2022]: `${FOLDER}madad-2022-award.png`,
  [DeveloperAwardType.Duns2018]: `${FOLDER}duns-100-2018-award.png`,
  [DeveloperAwardType.Duns2019]: `${FOLDER}duns-100-2019-award.png`,
  [DeveloperAwardType.Duns2020]: `${FOLDER}duns-100-2020-award.png`,
  [DeveloperAwardType.Duns2021]: `${FOLDER}duns-100-2021-award.png`,
  [DeveloperAwardType.Duns2022]: `${FOLDER}duns-100-2022-award.png`,
};


const Award: React.FC<{ award: IDeveloperAward; }> = ({ award }) => {
  const { type, imageUrl, title, description, linkUrl } = award;
  const constructImageURI = useImageUrlBuilder();
  const awardImagePath = awardTypeToImagePathMap[type];
  const image = awardImagePath ? constructImageURI(awardImagePath, { width: IMAGE_SIZE }) : null;
  const awardInner = (
    <Styled.AwardItemWrapper>
      <Styled.AwardImage>
        {image
          ? <img src={image}/>
          : (
            imageUrl
              ? <img src={constructImageURI(imageUrl, { width: IMAGE_SIZE, height: IMAGE_SIZE })} />
              : <AwardIcon />
          )
        }
      </Styled.AwardImage>
      <Styled.AwardInner>
        <Text weight="medium">{title}</Text>
        {description ? <SmallText>{description}</SmallText> : null}
      </Styled.AwardInner>
    </Styled.AwardItemWrapper>
  );

  return linkUrl ? (
    <Styled.AwardItemRoot>
      <AnchorMiddleware href={linkUrl}>
        {(href) => (
          <a href={href} rel="nofollow" target="_blank">{awardInner}</a>
        )}
      </AnchorMiddleware>
    </Styled.AwardItemRoot>
  ) : awardInner;
};

interface AwardsProps {
  awards: IDeveloperAward[];
}

const LIMIT_TO_SHOW = 3;

export const Awards: React.FC<AwardsProps> = ({ awards }) => {
  const { t } = useLocale();
  const screen = useScreenBreakpoint();
  const [ isOpened, setIsOpened ] = useState(false);
  const isMobile = screen === 1;
  const isTablet = screen === 2;

  if (!awards || !awards.length) return null;

  const moreThanLimitToShow = isMobile && awards.length > LIMIT_TO_SHOW;
  const handleClick = () => {
    setIsOpened(!isOpened);
  };

  const nonAnimatedCount = isTablet ? NON_ANIMATED_COUNT_TAB : NON_ANIMATED_COUNT;
  const maybeDesktopAnimation = awards.length > nonAnimatedCount && !isMobile;

  let items = awards;
  if (maybeDesktopAnimation) {
    items = [ ...awards, ...awards, ...awards ];
  }

  const list = useMemo(() => {
    const itemList = moreThanLimitToShow && !isOpened ? items.slice(0, LIMIT_TO_SHOW) : items;
    return itemList.map((award, i) => <Award award={award} key={i}/>);
  }, [ isOpened, moreThanLimitToShow, items ]);

  return (
    <>
      <SectionWrapper noTopMargin>
        <SubsectionWrapper withBorder={isMobile}>
          <SectionTitle>{t('developerPage.mainInfo.awards.title')}</SectionTitle>
          {maybeDesktopAnimation ? null : (
            <Styled.AwardsWrapper>
              {list}
              {moreThanLimitToShow ? (
                <Styled.ExpandButtonWrapper>
                  <Styled.ExpandButton onClick={handleClick} isOpened={isOpened}>{t('developerPage.mainInfo.awards.more', { isOpened })}<ChevronDown /></Styled.ExpandButton>
                </Styled.ExpandButtonWrapper>
              ) : null}
            </Styled.AwardsWrapper>
          )}
        </SubsectionWrapper>
      </SectionWrapper>
      {maybeDesktopAnimation ? (
        <Marquee>{list}</Marquee>
      ) : null}
    </>
  );
};
